/**
 * @file Generic object pool.
 *
 * The renderer, the particle emitter and any system that would otherwise
 * allocate per frame borrow from a pool instead, so the GC stays quiet and the
 * frame budget stays predictable (GDD §19).
 */

/**
 * @template T
 */
export class ObjectPool {
  /**
   * @param {() => T} factory Creates a brand new instance when the pool is dry.
   * @param {(item: T) => void} [reset] Called on release, before storing.
   * @param {number} [prewarm] How many instances to allocate up front.
   */
  constructor(factory, reset = undefined, prewarm = 0) {
    /** @type {() => T} @private */
    this._factory = factory;
    /** @type {((item: T) => void) | undefined} @private */
    this._reset = reset;
    /** @type {T[]} @private */
    this._free = [];
    /** @type {number} Total instances ever created, for diagnostics. */
    this.created = 0;
    /** @type {number} Instances currently handed out. */
    this.active = 0;

    for (let i = 0; i < prewarm; i++) {
      this._free.push(this._create());
    }
  }

  /** @returns {T} @private */
  _create() {
    this.created++;
    return this._factory();
  }

  /**
   * Borrows an instance.
   * @returns {T}
   */
  acquire() {
    this.active++;
    return this._free.pop() ?? this._create();
  }

  /**
   * Returns an instance to the pool.
   * @param {T} item Previously acquired instance.
   * @returns {void}
   */
  release(item) {
    if (item === undefined || item === null) return;
    this._reset?.(item);
    this._free.push(item);
    this.active = Math.max(0, this.active - 1);
  }

  /**
   * Drops every free instance, optionally disposing them.
   * @param {(item: T) => void} [dispose] Called on each pooled instance.
   * @returns {void}
   */
  clear(dispose = undefined) {
    if (dispose) for (const item of this._free) dispose(item);
    this._free.length = 0;
  }

  /** @returns {number} Instances currently idle in the pool. */
  get size() {
    return this._free.length;
  }
}
